import Facade from '@/Facade'
import GameSignals from '@/signals/GameSignals'
import { ISound } from '@modules/src/interfaces/ISound'
import { ModulesFacade } from '@modules/src/ModulesFacade'
import { isSoundOnSiteCode } from '@modules/src/operatorConfig'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { Howl, Howler } from 'howler'
import { SoundVolume } from '@/managers/sound/SoundVolume'
import { SoundEvent } from '@modules/src/enums/SoundEvent'
import gsap from 'gsap'
import { RestoreUtil } from '@/utils/RestoreUtil'
import { Model } from '@/model/Model'
import { buildAssetUrl } from '@/utils/AssetsUrlUtil'
import { IS_DEBUG_BUILD } from '@/utils/RuntimeEnv'
import { ShowRegularWinningsData } from '@/modules'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { resolveJackpotCoinTier } from '@/utils/JackpotCoinType'

export class SoundManager {
    public static model: Model
    public static muteStates: Record<string, boolean>
    public static readonly CROSS_FADE_DURATION: number = 1200
    public static readonly FS_END_THEME_FADE_DURATION: number = 1000
    
    //## FADE DURATIONS..!
    public static readonly BACKGROUND_FADE_IN_DURATION: number = 1000
    public static readonly BACKGROUND_FADE_OUT_DURATION: number = 1000

    public static readonly BIG_WIN_FADE_OUT: number = 1000
    public static readonly BIG_WIN_FADE_IN: number = 500
    public static readonly WIN_LAYER_FADE_DURATION: number = 2000
    public static readonly STOP_WIN_LAYER_FADE_DURATION: number = 2000
    public static readonly DURATION_BEFORE_BIG_WIN_END: number = 500
    public static readonly SUSPENSE_STOP_DURATION: number = 500

    //## Debug only: prints every sound as it starts. Off in production builds.
    private static readonly LOG_PLAYED_SOUNDS: boolean = IS_DEBUG_BUILD

    //## Background music ducking for commendations.
    //## The music drops to silence for the whole presentation, then returns: 1000ms down, held at
    //## 0 across every tier of the climb, 500ms back up. Kept separate from BIG_WIN_FADE_IN/OUT,
    //## whose names read backwards for this use (FADE_IN was driving the fade down).
    public static readonly COMMENDATION_MUSIC_FADE_OUT_MS: number = 1000
    public static readonly COMMENDATION_MUSIC_FADE_IN_MS: number = 1000
    public static readonly COMMENDATION_DUCK_VOLUME_BASE_GAME: number = 0
    public static readonly COMMENDATION_DUCK_VOLUME_FREE_GAME: number = 0
    private static commendationDuckedVolumes: WeakMap<Howl, number> = new WeakMap<Howl, number>()


    public static duckForCommendation(sound: Howl, duckedVolume: number): void {
        if (SoundManager.commendationDuckedVolumes.has(sound)) {
            return
        }

        const current = Number(sound.volume())
        if (!Number.isFinite(current) || current <= 0) {
            return
        }

        SoundManager.commendationDuckedVolumes.set(sound, current)
        sound.fade(current, Math.min(duckedVolume, current), SoundManager.COMMENDATION_MUSIC_FADE_OUT_MS)
    }

    public static restoreAfterCommendation(sound: Howl): void {
        const previous = SoundManager.commendationDuckedVolumes.get(sound)
        if (previous === undefined) {
            return
        }

        SoundManager.commendationDuckedVolumes.delete(sound)
        sound.fade(Number(sound.volume()), previous, SoundManager.COMMENDATION_MUSIC_FADE_IN_MS)
    }

    public static isBigWinEndSoundPlay: boolean
    public static lpWinSoundIndex: number
    public static hpWinSoundIndex: number
    public static masterVolume: number
    private static scatterLandPlayedReels: Set<number> = new Set<number>()
    private static fsCollectSfxPlayed = {
        glow: false,
        move: false,
        ladder: false,
    }
    private static readonly COMMENDATION_SOUND_BY_LEVEL = ['bigWin', 'megaWin', 'ultraWin', 'ultraMegaWin'] as const
    private static currentCommendationLevel = 0
    private static commendationHighestOnly = false
    /** All tier Howls handle the same signal — resolve once per emission tick. */
    private static commendationResolveGeneration = 0
    private static commendationResolvedAtGeneration = -1
    private static commendationResolveTier: number | null = null
    private _soundGroupsPollingId?: number
    /** Audio silenced from outside the settings — host page or the panel's speaker button. */
    private _isAudioSuspended = false

    /** Resolve which commendation tier SFX to play for showBigWin / startBigWinLevel. */
    private static resolveCommendationTierToPlay(args: any): number | null {
        const generation = SoundManager.commendationResolveGeneration
        if (SoundManager.commendationResolvedAtGeneration === generation) {
            return SoundManager.commendationResolveTier
        }

        const payload = args?.[0]
        const isShowBigWin =
            payload != null &&
            typeof payload === 'object' &&
            ('level' in payload || 'highestOnly' in payload || 'win' in payload)

        let tier: number | null = null

        if (isShowBigWin) {
            const level = Math.max(
                0,
                Math.min(SoundManager.COMMENDATION_SOUND_BY_LEVEL.length - 1, Number(payload.level) || 0)
            )
            const highestOnly = Boolean(payload.highestOnly)
            SoundManager.commendationHighestOnly = highestOnly
            SoundManager.currentCommendationLevel = highestOnly ? level : 0
            tier = SoundManager.currentCommendationLevel
        } else if (!SoundManager.commendationHighestOnly) {
            // startBigWinLevel: ladder only (highest-only already played its tier on showBigWin).
            SoundManager.currentCommendationLevel = Math.min(
                SoundManager.COMMENDATION_SOUND_BY_LEVEL.length - 1,
                SoundManager.currentCommendationLevel + 1
            )
            tier = SoundManager.currentCommendationLevel
        }

        SoundManager.commendationResolvedAtGeneration = generation
        SoundManager.commendationResolveTier = tier
        queueMicrotask(() => {
            SoundManager.commendationResolveGeneration++
        })
        return tier
    }

    private static playCommendationTierIfMatch(sound: Howl, args: any, tierLevel: number): void {
        if (SoundManager.resolveCommendationTierToPlay(args) !== tierLevel) {
            sound.stop()
            return
        }

        sound.stop()

        const payload = args?.[0]
        const isShowBigWin =
            payload != null &&
            typeof payload === 'object' &&
            ('level' in payload || 'highestOnly' in payload || 'win' in payload)

        if (isShowBigWin) {
            sound.play()
            return
        }

        // startBigWinLevel can be emitted from inside skipBigWin handlers — defer play so
        // skip STOP listeners cannot kill the next tier sound in the same tick.
        queueMicrotask(() => {
            if (SoundManager.currentCommendationLevel === tierLevel) {
                sound.play()
            }
        })
    }

    public constructor() {
        SoundManager.muteStates = {}
        SoundManager.isBigWinEndSoundPlay = false
        SoundManager.masterVolume = 1
    }

    private clampVolume = (value: number): number => {
        if (!Number.isFinite(value)) {
            return 1
        }

        return Math.max(0, Math.min(1, value))
    }

    private static getPrimaryWinningSymbolId(args: any): number | null {
        const payload = args?.[0]
        const directSymbolId = Number(payload?.symbol_id)

        if (Number.isFinite(directSymbolId)) {
            return directSymbolId
        }

        const data = payload as ShowRegularWinningsData | undefined
        const firstCombination = data?.combinations?.[0]
        const symbolId = Number(firstCombination?.symbol_id)

        if (!Number.isFinite(symbolId)) {
            return null
        }

        return symbolId
    }

    private static getSymbolTextureName(symbolId: number): string {
        return String(Facade.symbolsConfig?.indexes?.[String(symbolId)] || '').toLowerCase()
    }

    private static isWildLikeSymbolId(symbolId: number): boolean {
        if (!Number.isFinite(symbolId)) {
            return false
        }

        if (symbolId === SpecialSymbolType.MULTIPLIER) {
            return true
        }

        const textureName = SoundManager.getSymbolTextureName(symbolId)
        return textureName.includes('symbol-static_wild')
    }

    private static isLvMvHvTexture(textureName: string): boolean {
        return (
            textureName.includes('symbol-static_lv') ||
            textureName.includes('symbol-static_mv1') ||
            textureName.includes('symbol-static_mv2') ||
            textureName.includes('symbol-static_hv1') ||
            textureName.includes('symbol-static_hv2')
        )
    }

    private static isLvMvHvSymbolId(symbolId: number): boolean {
        if (!Number.isFinite(symbolId)) {
            return false
        }

        return SoundManager.isLvMvHvTexture(SoundManager.getSymbolTextureName(symbolId))
    }

    private static getLineByLineWinningPositions(args: any): number[][] {
        const payload = args?.[0]

        const sourcePositions = Array.isArray(payload?.symbols_positions) ? payload.symbols_positions : []
        const normalized = sourcePositions
            .filter((position: number[]) => Array.isArray(position) && position.length >= 2)
            .map((position: number[]) => [Number(position[0]), Number(position[1])])
            .filter((position: number[]) => Number.isFinite(position[0]) && Number.isFinite(position[1]))
            .sort((a: number[], b: number[]) => a[0] - b[0])

        const symbolCount = Number(payload?.symbol_count)
        if (Number.isFinite(symbolCount) && symbolCount > 0 && normalized.length > symbolCount) {
            return normalized.slice(0, symbolCount)
        }

        return normalized
    }

    private static getSymbolIdByGridPosition(position: number[]): number | null {
        const slotGrid = SoundManager.model?.spinData?.slot_grid?.[SoundManager.model?.cascadeIndex]
        const reelIndex = Number(position?.[0])
        const rowIndex = Number(position?.[1])
        const symbolId = Number(slotGrid?.[reelIndex]?.[rowIndex]?.id)

        return Number.isFinite(symbolId) ? symbolId : null
    }

    private static getScatterLandedEventData(args: any): { scatterCount: number; reelIndex: number } | null {
        const scatterCount = Number(args?.[0])
        const reelIndex = Number(args?.[1])

        if (!Number.isFinite(scatterCount) || !Number.isFinite(reelIndex)) {
            return null
        }

        if (scatterCount < 1 || reelIndex < 0) {
            return null
        }

        return { scatterCount, reelIndex }
    }

    private static reelContainsScatter(reelIndex: number): boolean {
        const slotGrid = SoundManager.model?.spinData?.slot_grid?.[SoundManager.model?.cascadeIndex]
        const reelSymbols = slotGrid?.[reelIndex]

        if (!Array.isArray(reelSymbols)) {
            return false
        }

        return reelSymbols.some((symbol: any) => Number(symbol?.id) === SpecialSymbolType.SCATTER)
    }

    /** Base game / free spins prize coin. Jackpot coins are excluded — they have their own cue. */
    private static isBaseCoinLandPayload(args: any): boolean {
        const payload = args?.[0]
        const symbolIndex = Number(payload?.symbolIndex)
        const symbolKey = String(payload?.symbolKey || '').toLowerCase()

        if (symbolIndex !== SpecialSymbolType.MULTIPLIER) {
            return false
        }

        if (resolveJackpotCoinTier(payload?.coinType)) {
            return false
        }

        return symbolKey === 'k'
    }

    /**
     * Base game / free spins jackpot coin.
     *
     * Deliberately does not require symbolKey === 'k' the way the prize-coin check does:
     * jackpot coins are identified by coinType alone, so that gate would drop them.
     */
    private static isBaseJackpotCoinLandPayload(args: any): boolean {
        const payload = args?.[0]

        if (Number(payload?.symbolIndex) !== SpecialSymbolType.MULTIPLIER) {
            return false
        }

        return resolveJackpotCoinTier(payload?.coinType) !== null
    }

    private static resetFsCollectSfxFlags(): void {
        SoundManager.fsCollectSfxPlayed.glow = false
        SoundManager.fsCollectSfxPlayed.move = false
        SoundManager.fsCollectSfxPlayed.ladder = false
    }

    private static getLineByLineSymbolIds(args: any): number[] {
        const positions = SoundManager.getLineByLineWinningPositions(args)
        return positions
            .map((position) => SoundManager.getSymbolIdByGridPosition(position))
            .filter((symbolId): symbolId is number => Number.isFinite(symbolId))
    }

    private static getLineByLineSymbolId(args: any): number | null {
        const lineSymbolIds = SoundManager.getLineByLineSymbolIds(args)
        const firstLvMvHv = lineSymbolIds.find((symbolId) => SoundManager.isLvMvHvSymbolId(symbolId))
        if (Number.isFinite(firstLvMvHv)) {
            return firstLvMvHv
        }

        const firstNonWild = lineSymbolIds.find((symbolId) => !SoundManager.isWildLikeSymbolId(symbolId))
        if (Number.isFinite(firstNonWild) && SoundManager.isLvMvHvSymbolId(firstNonWild)) {
            return firstNonWild
        }

        const primarySymbolId = SoundManager.getPrimaryWinningSymbolId(args)
        if (Number.isFinite(primarySymbolId) && SoundManager.isLvMvHvSymbolId(primarySymbolId)) {
            return primarySymbolId
        }

        return null
    }

    private static isLineByLineWildWin(args: any): boolean {
        const lineSymbolIds = SoundManager.getLineByLineSymbolIds(args)
        if (lineSymbolIds.some((symbolId) => SoundManager.isWildLikeSymbolId(symbolId))) {
            return true
        }

        const primarySymbolId = SoundManager.getPrimaryWinningSymbolId(args)
        return Number.isFinite(primarySymbolId) && SoundManager.isWildLikeSymbolId(primarySymbolId)
    }

    private static resolveLineByLineWinSoundKey(args: any): string | null {
        const symbolId = SoundManager.getLineByLineSymbolId(args)
        if (!Number.isFinite(symbolId)) {
            return null
        }

        const textureName = SoundManager.getSymbolTextureName(symbolId)

        if (textureName.includes('symbol-static_hv1')) {
            return 'highValue1Won_StarEagle'
        }

        if (textureName.includes('symbol-static_hv2')) {
            return 'highValue2Won_FireEagle'
        }

        if (textureName.includes('symbol-static_mv1')) {
            return 'highValue3Won_IceEagle'
        }

        if (textureName.includes('symbol-static_mv2')) {
            return 'highValue4Won_LeafEagle'
        }

        if (textureName.includes('symbol-static_lv')) {
            return 'LVSymbolWon'
        }

        return null
    }

    private onSetMasterVolume = (value: number) => {
        const volume = this.clampVolume(value)
        SoundManager.masterVolume = volume

        if (Facade.defaultConfig) {
            Facade.defaultConfig.volume = volume
        }

        Howler.volume(volume)

        // Re-assert the per-group choices instead of deriving them from the master volume. Forcing
        // both groups on whenever the volume was above zero silently revived a group the player had
        // just switched off, because turning one toggle off restores a non-zero master volume.
        this.applyStoredAudioStatesToGroups()
    }

    private syncHowlerMuteFallback = () => {
        const soundsEnabled = SoundManager.muteStates.sounds ?? Facade.defaultConfig?.sound ?? true
        const musicEnabled = SoundManager.muteStates.music ?? Facade.defaultConfig?.music ?? true
        const hasAnyEnabled = !!soundsEnabled || !!musicEnabled

        Howler.mute(!hasAnyEnabled)
    }

    /**
     * Silences everything without touching the player's per-group choices, so resuming can put back
     * exactly what they had. Raised by the panel's speaker button and by the host page through the
     * integration bridge — a casino frame pauses, resumes and toggles audio on the game at will.
     */
    private onSuspendAudio = () => {
        this._isAudioSuspended = true

        ModulesFacade.soundGroups?.sounds?.muteGroup()
        ModulesFacade.soundGroups?.music?.muteGroup()

        Howler.mute(true)
    }

    /**
     * Restores the stored choices rather than switching both groups on. Turning them on was what
     * revived a group the player had just silenced: the host sends this on resume, on regaining
     * focus and on its own audio toggle, none of which knows anything about the game's settings.
     */
    private onResumeAudio = () => {
        this._isAudioSuspended = false
        this.applyStoredAudioStatesToGroups()
    }

    private applyStoredAudioStatesToGroups = () => {
        // A suspended game is silent whatever the stored states say; they are put back on resume.
        if (this._isAudioSuspended) {
            return
        }

        if (!ModulesFacade.soundGroups) {
            this.syncHowlerMuteFallback()
            return
        }

        const soundsEnabled = SoundManager.muteStates.sounds ?? Facade.defaultConfig?.sound ?? true
        const musicEnabled = SoundManager.muteStates.music ?? Facade.defaultConfig?.music ?? true

        const soundsGroup = ModulesFacade.soundGroups.sounds
        const musicGroup = ModulesFacade.soundGroups.music

        if (soundsGroup) {
            soundsEnabled ? soundsGroup.unMuteGroup() : soundsGroup.muteGroup()
        }

        if (musicGroup) {
            musicEnabled ? musicGroup.unMuteGroup() : musicGroup.muteGroup()
        }

        this.syncHowlerMuteFallback()
    }

    private static isFeatureMusicContextActive(): boolean {
        const isFsRound = Boolean(SoundManager.model?.roundState?.isFreeSpins)
        const isFreeRoundsSession = Boolean(SoundManager.model?.freeRoundsActive?.())
        const isHoldAndWinActive = Boolean(SoundManager.model?.spinState?.isHoldAndWinActive)

        return isFsRound || isFreeRoundsSession || isHoldAndWinActive
    }

    private static isFreeSpinsEndBanner(): boolean {
        return (
            Boolean(SoundManager.model?.roundState?.isFreeSpins) &&
            !Boolean(SoundManager.model?.spinState?.isHoldAndWinActive)
        )
    }

    /**
     * Starts the track and ramps it up. Playback is what the transition controls — the fade is
     * only the ramp — so a track that is not playing is started here rather than left running
     * silently. Any stop still armed from a previous fade-out is cancelled first, otherwise it
     * would fire moments later and kill the track that was just restarted.
     */
    private static fadeInBaseTheme(sound: Howl, durationMs: number): void {
        gsap.killTweensOf(sound)

        if (!sound.playing()) {
            sound.play()
            sound.loop(true)
            sound.volume(0)
        }

        const currentVolume = Number(sound.volume())
        const fromVolume = Number.isFinite(currentVolume) ? currentVolume : 0
        if (fromVolume >= SoundVolume.MAIN_THEME) {
            return
        }

        sound.fade(fromVolume, SoundVolume.MAIN_THEME, durationMs)
    }

    /**
     * Ramps the track down and then stops it — used by both background themes, so neither is
     * left looping inaudibly once it has handed over.
     */
    private static fadeOutAndStopTheme(sound: Howl, durationMs: number): void {
        const currentVolume = Number(sound.volume())
        const fromVolume = Number.isFinite(currentVolume) ? currentVolume : SoundVolume.FS_THEME
        if (fromVolume <= 0 && !sound.playing()) {
            return
        }

        // Drop any stop still pending from an earlier fade. Two stop timers with different
        // deadlines means the shorter one cuts a ramp that is still audible — an audible click.
        gsap.killTweensOf(sound)

        sound.fade(fromVolume, 0, durationMs)
        gsap.to(sound, {
            duration: durationMs / 1000,
            onComplete: () => {
                sound.stop()
            },
        })
    }


    //## Background crossfade is a special case: the base theme and free spins theme are both
    private static backgroundCrossfadeUntil: number = 0

    /** Claims both tracks for the longer half, so neither can be restarted mid-ramp. */
    private static beginBackgroundCrossfade(): void {
        const durationMs = Math.max(
            SoundManager.BACKGROUND_FADE_IN_DURATION,
            SoundManager.BACKGROUND_FADE_OUT_DURATION
        )

        SoundManager.backgroundCrossfadeUntil = performance.now() + durationMs
    }

    private static isBackgroundCrossfadeActive(): boolean {
        return performance.now() < SoundManager.backgroundCrossfadeUntil
    }

    /** durationMs defaults to the Hold & Win crossfade; free spins passes its own. */
    private static startFeatureBackgroundLoop(
        sound: Howl,
        durationMs: number = SoundManager.CROSS_FADE_DURATION
    ): void {
        // Cancel a pending stop from a previous fade-out before restarting.
        gsap.killTweensOf(sound)

        if (!sound.playing()) {
            sound.play()
            sound.loop(true)
            sound.volume(0)
        }

        const currentVolume = Number(sound.volume())
        const safeCurrentVolume = Number.isFinite(currentVolume) ? currentVolume : 0

        if (safeCurrentVolume < SoundVolume.FS_THEME) {
            sound.fade(safeCurrentVolume, SoundVolume.FS_THEME, durationMs)
            return
        }

        sound.volume(SoundVolume.FS_THEME)
    }

    private static stopFeatureBackgroundLoop(
        sound: Howl,
        durationMs: number = SoundManager.CROSS_FADE_DURATION
    ): void {
        if (SoundManager.isFeatureMusicContextActive()) {
            return
        }

        const currentVolume = Number(sound.volume())
        const safeCurrentVolume = Number.isFinite(currentVolume) ? currentVolume : SoundVolume.FS_THEME

        sound.fade(safeCurrentVolume, 0, durationMs)
        gsap.to(sound, {
            duration: durationMs / 1000,
            onComplete: () => {
                sound.stop()
            },
        })
    }

    private waitForSoundGroupsInitialization = () => {
        if (this._soundGroupsPollingId) {
            window.clearInterval(this._soundGroupsPollingId)
            this._soundGroupsPollingId = undefined
        }

        this.instrumentPlayLogging()

        this._soundGroupsPollingId = window.setInterval(() => {
            if (!ModulesFacade.soundGroups) {
                return
            }

            this.applyStoredAudioStatesToGroups()
            this.instrumentPlayLogging()

            if (this._soundGroupsPollingId) {
                window.clearInterval(this._soundGroupsPollingId)
                this._soundGroupsPollingId = undefined
            }
        }, 250)
    }

   
    //## Debug-only: wrap every sound's play() to log its group/name/volume and why it might be silent.
    private instrumentPlayLogging = (): void => {
        if (!SoundManager.LOG_PLAYED_SOUNDS || !ModulesFacade.soundGroups) {
            return
        }

        for (const groupName of Object.keys(ModulesFacade.soundGroups)) {
            const group = ModulesFacade.soundGroups[groupName]

            for (const soundName of Object.keys(group?.sounds || {})) {
                const sound = group.sounds[soundName] as any

                if (!sound || sound.__dgPlayLogged) {
                    continue
                }

                sound.__dgPlayLogged = true
                const play = sound.play.bind(sound)

                sound.play = (...args: any[]) => {
                    const trackId = play(...args)
                    SoundManager.logPlayedSound(groupName, soundName, sound, group)
                    return trackId
                }
            }
        }
    }

    //## Print What sound is playing, and why it might be silent. This is a debug-only tool to help
    private static logPlayedSound(groupName: string, soundName: string, sound: any, group: any): void {
        const soundVolume = Number(sound.volume?.() ?? 1)
        const masterVolume = Number(Howler.volume())
        const silentBecause: string[] = []

        if (group?.muted) {
            silentBecause.push(`group "${groupName}" muted`)
        }

        if (sound.mute?.()) {
            silentBecause.push('sound muted')
        }

        if (!(soundVolume > 0)) {
            silentBecause.push('sound volume 0')
        }

        if (!(masterVolume > 0)) {
            silentBecause.push('master volume 0')
        }

        if ((Howler as any)._muted) {
            silentBecause.push('Howler muted')
        }

        const status = silentBecause.length > 0 ? `SILENT — ${silentBecause.join(', ')}` : 'audible'

        console.log(
            `%c[Sound] ▶ ${groupName}/${soundName}`,
            'color:#4ea1ff;font-weight:bold',
            `| vol ${soundVolume} × master ${masterVolume} | ${status}`
        )
    }

    private onToggleSoundGroup = (group: string, value: boolean) => {
        // Early return if defaultConfig is not loaded yet
        if (!Facade.defaultConfig) {
            console.warn('SoundManager: defaultConfig not yet loaded')
            return
        }

        const soundGroup = ModulesFacade.soundGroups?.[group]
        SoundManager.muteStates[group] = value

        if (group === 'sounds') {
            Facade.defaultConfig.sound = value
        } else {
            Facade.defaultConfig.music = value
        }

        if (soundGroup != null) {
            if (value) {
                soundGroup.unMuteGroup()
            } else {
                soundGroup.muteGroup()
            }
        }

        this.syncHowlerMuteFallback()
    }

    private onToggleSoundGroups = (value: boolean) => {
        this.onToggleSoundGroup('sounds', value)
        this.onToggleSoundGroup('music', value)
    }

    private onVisibilityChange = () => {
        Howler.mute(document.visibilityState === 'hidden')
    }

    private initialize(model: Model): void {
        SoundManager.model = model
        window.addEventListener('visibilitychange', this.onVisibilityChange)

        GameSignals.toggleSoundGroup.connect(this.onToggleSoundGroup)
        GameSignals.setMasterVolume.connect(this.onSetMasterVolume)
        GameSignals.unMute.connect(this.onResumeAudio)
        GameSignals.mute.connect(this.onSuspendAudio)

        const localStorageData = {
            music: RestoreUtil.getKeyData(RestoreUtil.MUSIC_KEY),
            sound: RestoreUtil.getKeyData(RestoreUtil.SOUND_KEY),
            volume: RestoreUtil.getKeyData(RestoreUtil.VOLUME_KEY),
        }

        const fallbackVolume = typeof Facade.defaultConfig?.volume === 'number' ? Facade.defaultConfig.volume : 1
        const persistedVolume = Number(localStorageData.volume)
        const fallbackMuted = localStorageData.sound === 0 && localStorageData.music === 0

        // Sound defaults OFF for every operator. A small allow-list of site codes
        // (soundOnSiteCodes in operatorConfig.js) forces sound ON on every load,
        // ignoring any previously persisted/muted state.
        const soundOnByDefault = isSoundOnSiteCode(Facade.urlParams?.siteCode || Facade.urlParams?.site_code)
        const soundOnVolume = fallbackVolume > 0 ? fallbackVolume : 1

        const initialVolume = this.clampVolume(
            soundOnByDefault
                ? soundOnVolume
                : Number.isFinite(persistedVolume)
                  ? persistedVolume
                  : fallbackMuted
                    ? 0
                    : fallbackVolume
        )

        this.onSetMasterVolume(initialVolume)
        RestoreUtil.setKeyData(RestoreUtil.VOLUME_KEY, `${initialVolume}`)
        RestoreUtil.setKeyData(RestoreUtil.SOUND_KEY, `${+(initialVolume > 0)}`)
        RestoreUtil.setKeyData(RestoreUtil.MUSIC_KEY, `${+(initialVolume > 0)}`)

        this.waitForSoundGroupsInitialization()
    }

    public static createConfigPath(): string {
        const channelType = DeviceUtils.isMobileDevice() ? 'mobile' : 'desktop'
        return buildAssetUrl(`/assets/sounds/sounds_${channelType}.json`)
    }

    public static initializeHowlerConfig(model: Model): void {
        new SoundManager().initialize(model)
        Howler.autoSuspend = false
    }

    public static createSounds(): ISound[] {
        return [
            {
                group: 'sounds',
                name: 'gen_sounds_bet_decreasestd',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.changeBet],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] < this.model.currentBaseBet && args[0] !== +this.model.bets.at(0).toFixed(2)) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_sounds_bet_increasestd',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.changeBet],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (args[0] > this.model.currentBaseBet && args[0] !== +this.model.bets.at(-1).toFixed(2)) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_sounds_bet_increase_decrease_max',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.changeBet],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                args[0] === +this.model.bets.at(0).toFixed(2) ||
                                args[0] === +this.model.bets.at(-1).toFixed(2)
                            ) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'gen_ui_sound',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.buttonClicked],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (
                                args[0] &&
                                args[0] !== 'ui-footer-bet_prev' &&
                                args[0] !== 'ui-footer-bet_next' &&
                                args[0] !== 'ui-game-ctrl-spin'
                            ) {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.playUIGenSound],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'buyBonusBtn',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.openBuyBonusPopup],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'bonusPickedFs',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).buyBonusConfirmButtonClicked],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'coinSymbolLand',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).holdAndWinCoinLanded],
                        event: SoundEvent.PLAY,
                    },
                    {
                        onGameSignalsTriggered: [(GameSignals as any).baseGameCoinLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            const isHoldAndWinActive = Boolean(this.model?.spinState?.isHoldAndWinActive)
                            if (isHoldAndWinActive) {
                                return
                            }

                            if (!SoundManager.isBaseCoinLandPayload(args)) {
                                return
                            }

                            sound.play()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'HandWjackpotland',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        // Hold & Win: jackpot coin landing in a cell.
                        onGameSignalsTriggered: [(GameSignals as any).holdAndWinJackpotCoinLanded],
                        event: SoundEvent.PLAY,
                    },
                    {
                        // Base game / free spins: jackpot coin landing on the reels. Prize coins
                        // on the same signal fall through to coinSymbolLand instead.
                        onGameSignalsTriggered: [(GameSignals as any).baseGameCoinLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            const isHoldAndWinActive = Boolean(this.model?.spinState?.isHoldAndWinActive)
                            if (isHoldAndWinActive) {
                                return
                            }

                            if (!SoundManager.isBaseJackpotCoinLandPayload(args)) {
                                return
                            }

                            sound.play()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'jackpotBox',
                volume: SoundVolume.APPEAR_POPUP,
                loop: false,
                events: [
                    {
                        // When mini/minor/major/grand popup is shown during collect.
                        onGameSignalsTriggered: [(GameSignals as any).jackpotPopupStart],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'HandWmultiplierfly',
                volume: SoundVolume.MULTIPLIER_TRAIL,
                loop: false,
                events: [
                    {
                        // Value label starts flying from the coin toward the ticker.
                        onGameSignalsTriggered: [(GameSignals as any).holdAndWinCollectFlyStart],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'HandWmultiplieradd',
                volume: SoundVolume.MULTIPLIER_JAR,
                loop: false,
                events: [
                    {
                        // Value label hits the ticker box and is added to the collect total.
                        onGameSignalsTriggered: [(GameSignals as any).holdAndWinCollectFlyHit],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'glowingBallSound',
                volume: SoundVolume.MULTIPLIER_COLLECT,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [
                            (GameSignals as any).fsCollectSequenceRequested,
                            GameSignals.startSpin,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: () => {
                            SoundManager.resetFsCollectSfxFlags()
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.fsTopMultiplierCollectFly],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl) => {
                            if (!this.model?.roundState?.isFreeSpins) {
                                return
                            }

                            if (SoundManager.fsCollectSfxPlayed.glow) {
                                return
                            }

                            SoundManager.fsCollectSfxPlayed.glow = true

                            sound.play()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'coinMovestoLadder',
                volume: SoundVolume.MULTIPLIER_TRAIL,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).fsCoinCollectMoveToLadder],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl) => {
                            if (!this.model?.roundState?.isFreeSpins) {
                                return
                            }

                            if (SoundManager.fsCollectSfxPlayed.move) {
                                return
                            }

                            SoundManager.fsCollectSfxPlayed.move = true

                            sound.play()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'ladderValueSound',
                volume: SoundVolume.MULTIPLIER_JAR,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.updateJarValue, GameSignals.multiplierLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl) => {
                            const isFreeSpins = Boolean(this.model?.roundState?.isFreeSpins)
                            const isHoldAndWin = Boolean(this.model?.spinState?.isHoldAndWinActive)

                            if (!isFreeSpins || isHoldAndWin) {
                                return
                            }

                            if (SoundManager.fsCollectSfxPlayed.ladder) {
                                return
                            }

                            SoundManager.fsCollectSfxPlayed.ladder = true

                            sound.play()
                        },
                    },
                    {
                        onGameSignalsTriggered: [(GameSignals as any).fsCollectSequenceComplete],
                        event: SoundEvent.CUSTOM,
                        callback: () => {
                            SoundManager.resetFsCollectSfxFlags()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'jackpotUpgradeSound',
                volume: SoundVolume.APPEAR_POPUP,
                loop: false,
                events: [
                    {
                        // Plays with the jackpot upgrade spine (eng_in → loop → out).
                        onGameSignalsTriggered: [(GameSignals as any).jackpotLadderUpgradeStart],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'music',
                name: 'backgroundPlay',
                volume: 0,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.play()
                            sound.loop(true)
                            /**Strange fix, when tapping on the screen during recovery, the music worked with the music button turned off**/
                            if (this.model.isFreeSpinsRestore) {
                                sound.volume(0)
                            } else {
                                sound.fade(0, SoundVolume.MAIN_THEME, SoundManager.CROSS_FADE_DURATION)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!this.model.isFreeSpinsRestore) {
                                sound.fade(SoundVolume.MAIN_THEME, 0, SoundManager.CROSS_FADE_DURATION / 2)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.beginBackgroundCrossfade()
                            SoundManager.fadeOutAndStopTheme(sound, SoundManager.BACKGROUND_FADE_OUT_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            (GameSignals as any).holdAndWinTransitionStart,
                            (GameSignals as any).holdAndWinStartSpin,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (SoundManager.isBackgroundCrossfadeActive()) {
                                return
                            }

                            SoundManager.fadeOutAndStopTheme(sound, SoundManager.BACKGROUND_FADE_OUT_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.stopFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.fadeInBaseTheme(sound, SoundManager.CROSS_FADE_DURATION)
                        },
                    },
                    {
                        //## Basegame sound Fadein and FS BG fade out
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!SoundManager.isFreeSpinsEndBanner()) {
                                return
                            }

                            SoundManager.beginBackgroundCrossfade()
                            SoundManager.fadeInBaseTheme(sound, SoundManager.BACKGROUND_FADE_IN_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            GameSignals.showBaseGameBackground,
                            (GameSignals as any).holdAndWinStop,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (SoundManager.isFeatureMusicContextActive()) {
                                return
                            }

                            // The free-spins-end crossfade already owns this fade.
                            if (SoundManager.isBackgroundCrossfadeActive()) {
                                return
                            }

                            // Hold & Win exit — same 1000ms as every other background transition.
                            SoundManager.fadeInBaseTheme(sound, SoundManager.BACKGROUND_FADE_IN_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            // if (!this.model.isBonusGameNow) {
                            //     sound.fade(SoundVolume.MAIN_THEME, 0, SoundManager.BIG_WIN_FADE_IN)
                            // }

                            //## Ducks in every context — gating on isBonusGameNow left this track at full level under a Hold & Win commendation.
                            SoundManager.duckForCommendation(sound, SoundManager.COMMENDATION_DUCK_VOLUME_BASE_GAME)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            // if (!this.model.isBonusGameNow) {
                            //     sound.fade(0, SoundVolume.MAIN_THEME, SoundManager.BIG_WIN_FADE_OUT)
                            // }

                            //## Ungated to match the duck: restoring only in base game would strand the track at half level for the rest of a feature.
                            SoundManager.restoreAfterCommendation(sound)
                        },
                    },
                ],
            },
            {
                group: 'music',
                name: 'main_theme_win_layer_2',
                volume: 0,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.play()
                            sound.loop(true)
                            sound.volume(0)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startWinLayerMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.fade(0, SoundVolume.MAIN_WIN_THEME, SoundManager.WIN_LAYER_FADE_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.stopWinLayerMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (this.model.isBonusGameNow) {
                                sound.volume(0)
                            } else {
                                sound.fade(SoundVolume.MAIN_WIN_THEME, 0, SoundManager.STOP_WIN_LAYER_FADE_DURATION)
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            sound.volume(0)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            //## The win layer can still be running when Hold & Win starts — it is
                            //## only stopped by startFreeSpinsMusic — so isBonusGameNow blocked the
                            //## duck exactly when a commendation played over it.
                            SoundManager.duckForCommendation(sound, SoundManager.COMMENDATION_DUCK_VOLUME_BASE_GAME)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.restoreAfterCommendation(sound)
                        },
                    },
                ],
            },
            {
                group: 'music',
                name: 'backgroundFS',
                volume: 0,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.startFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            /**Strange fix, when tapping on the screen during recovery, the music worked with the music button turned off**/
                            if (this.model.isFreeSpinsRestore) {
                                if (!sound.playing()) {
                                    sound.play()
                                    sound.loop(true)
                                }
                                sound.volume(SoundVolume.FS_THEME)
                            } else {
                                SoundManager.startFeatureBackgroundLoop(sound)
                            }
                        },
                    },
                    {
                        //## Fallback for a Hold & Win that starts with an intro popup. Stands down when
                        onGameSignalsTriggered: [
                            (GameSignals as any).featureIntroContinue,
                            GameSignals.restoreFreeSpins,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.startFeatureBackgroundLoop(sound, SoundManager.BACKGROUND_FADE_IN_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            (GameSignals as any).holdAndWinTransitionStart,
                            (GameSignals as any).holdAndWinStartSpin,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (SoundManager.isBackgroundCrossfadeActive()) {
                                return
                            }

                            SoundManager.startFeatureBackgroundLoop(sound, SoundManager.BACKGROUND_FADE_IN_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.stopFreeSpinsMusic],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.stopFeatureBackgroundLoop(sound)
                        },
                    },
                    {
                        //## Leaving free spins: Continue on the summary popup, paired with
                        //## backgroundPlay's fade-in on the same signal. Guarded because this
                        //## signal is shared with the Hold & Win summary.
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (!SoundManager.isFreeSpinsEndBanner()) {
                                return
                            }

                            SoundManager.fadeOutAndStopTheme(sound, SoundManager.BACKGROUND_FADE_OUT_DURATION)
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            GameSignals.showBaseGameBackground,
                            (GameSignals as any).holdAndWinStop,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            if (SoundManager.isBackgroundCrossfadeActive()) {
                                return
                            }

                            // Hold & Win exit — same 1000ms as every other background transition.
                            SoundManager.stopFeatureBackgroundLoop(sound, SoundManager.BACKGROUND_FADE_OUT_DURATION)
                        },
                    },
                    {
                        //## Ducks to COMMENDATION_DUCK_VOLUME_FREE_GAME 
                        onGameSignalsTriggered: [GameSignals.showBigWin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.duckForCommendation(sound, SoundManager.COMMENDATION_DUCK_VOLUME_FREE_GAME)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            SoundManager.restoreAfterCommendation(sound)
                        },
                    },
                ],
            },

            {
                group: 'sounds',
                name: 'wildSymbolWon',
                volume: SoundVolume.WILD_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.isLineByLineWildWin(args)) {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'highValue1Won_StarEagle',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.resolveLineByLineWinSoundKey(args) === 'highValue1Won_StarEagle') {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'highValue2Won_FireEagle',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.resolveLineByLineWinSoundKey(args) === 'highValue2Won_FireEagle') {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'highValue3Won_IceEagle',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.resolveLineByLineWinSoundKey(args) === 'highValue3Won_IceEagle') {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'highValue4Won_LeafEagle',
                volume: SoundVolume.HP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.resolveLineByLineWinSoundKey(args) === 'highValue4Won_LeafEagle') {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'LVSymbolWon',
                volume: SoundVolume.LP_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            if (SoundManager.resolveLineByLineWinSoundKey(args) === 'LVSymbolWon') {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'HVSymbolWon',
                volume: SoundVolume.HV_SYMBOL_WON,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).lineByLineWinStep],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            const winSoundKey = SoundManager.resolveLineByLineWinSoundKey(args)

                            if (winSoundKey !== null && winSoundKey !== 'LVSymbolWon') {
                                sound.play()
                            }
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                //## Total Win label.
                group: 'sounds',
                name: 'totalWin',
                volume: SoundVolume.TOTAL_WIN,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).showTotalWinLabel],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl) => {
                            sound.stop()
                            sound.play()
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            GameSignals.startSpin,
                            (GameSignals as any).bigWinPresentation,
                            GameSignals.showBigWin,
                            (GameSignals as any).lineByLineStepChanged,
                        ],
                        event: SoundEvent.STOP,
                    },
                ],
            },

            {
                group: 'sounds',
                name: 'bonusSymbolWon',
                volume: SoundVolume.SCATTER_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.activateScatters],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'HandWtriggered',
                volume: SoundVolume.SCATTER_TRIGGER,
                loop: false,
                events: [
                    {
                        // Play exactly when 6+ trigger coins start the Hold & Win trigger animation.
                        onGameSignalsTriggered: [(GameSignals as any).holdAndWinTriggerCoinWinStart],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'fsAwardPopup',
                volume: SoundVolume.INTRO_TRANSITION_SOUND,
                loop: false,
                events: [
                    {
                        // Play when feature intro popup becomes visible (both FS and H&W).
                        onGameSignalsTriggered: [(GameSignals as any).featureIntroDisplayRequested],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'bonusSymbolLand',
                volume: SoundVolume.SCATTER_LAND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.CUSTOM,
                        callback: () => {
                            SoundManager.scatterLandPlayedReels.clear()
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.scatterLanded],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl, args: any) => {
                            const scatterData = SoundManager.getScatterLandedEventData(args)

                            if (!scatterData) {
                                return
                            }

                            const { reelIndex } = scatterData

                            if (!SoundManager.reelContainsScatter(reelIndex)) {
                                return
                            }

                            if (SoundManager.scatterLandPlayedReels.has(reelIndex)) {
                                return
                            }

                            SoundManager.scatterLandPlayedReels.add(reelIndex)
                            sound.play()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'anticipation2',
                volume: SoundVolume.ANTICIPATION,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showSuspenseAnimation],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl) => {
                            //## One-shot clip: restart it for every anticipating reel, otherwise a
                            //## still-playing instance would swallow the next reel's trigger.
                            sound.stop()
                            sound.play()
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.hideSuspenseAnimation],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl) => {
                            sound.stop()
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            GameSignals.stopSpin,
                            GameSignals.reelsImmediatelyStopped,
                            GameSignals.errorStop,
                        ],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings: ISound, sound: Howl) => {
                            sound.stop()
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'feature_transition_2',
                volume: SoundVolume.INTRO_TRANSITION_SOUND,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.splashScreenButtonClicked],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            gsap.to(this, {
                                duration: 2,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.bonusStart, GameSignals.bonusStop],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'showFSEndPopUp',
                volume: SoundVolume.APPEAR_POPUP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl) => {

                            sound.stop()
                            sound.play()
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.showFreeSpinsSummaryComplete],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'counterLoop',
                volume: SoundVolume.BIG_WIN_COUNT_UP,
                loop: true,
                events: [
                    {
                        // One emit per commendation level — keep the loop running across the climb.
                        onGameSignalsTriggered: [(GameSignals as any).bigWinCounterStart],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl) => {
                            if (sound.playing()) {
                                return
                            }

                            // Sprite sounds only accept loop() once they have a track id, so it
                            // has to be set after play() — SoundEvent.PLAY does the same.
                            sound.play()
                            sound.loop(true)
                        },
                    },
                    {
                        onGameSignalsTriggered: [
                            (GameSignals as any).bigWinCounterComplete,
                            GameSignals.skipBigWin,
                            GameSignals.showBigWinComplete,
                        ],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'counterEnd',
                volume: SoundVolume.BIG_WIN_COUNT_UP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).bigWinCounterComplete],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'ultraMegaWinDisappear',
                volume: SoundVolume.BIG_WIN_END,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [(GameSignals as any).bigWinClosing],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl) => {
                            sound.stop()
                            sound.play()
                        },
                    },
                    {
                        //## The clip runs 2.58s. Nothing else cuts it now that the skipBigWin STOP
                        //## is gone — that stop was silencing the very case this sting is for.
                        onGameSignalsTriggered: [GameSignals.startSpin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'reelSpinStart',
                volume: SoundVolume.REEL_START,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.spinButtonClicked],
                        event: SoundEvent.PLAY,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'spinStopBtn',
                volume: SoundVolume.UI_BUTTONS,
                loop: false,
                events: [
                    {
                        // Not stopButtonClicked: that is the game's generic skip request, raised by a
                        // tap anywhere on the screen and twice for one press of the panel button, so
                        // the sample doubled on itself. spinStopPressed is the press alone.
                        onGameSignalsTriggered: [(GameSignals as any).spinStopPressed],
                        event: SoundEvent.PLAY,
                    },
                ],
            },

            {
                group: 'sounds',
                name: 'reel_stop_1',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.stopSpin],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            // Disabled: per-reel symbolLand_1..5 already provide reel stop SFX.
                            // Keeping this generic stop sound causes a perceived extra/misaligned reel hit.
                            return
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.reelsImmediatelyStopped],
                        event: SoundEvent.CUSTOM,
                        callback: (soundSettings, sound, signalData) => {
                            gsap.killTweensOf(this)
                            sound.stop()
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.errorStop],
                        event: SoundEvent.STOP,
                    },
                ],
            },

            {
                group: 'sounds',
                name: 'symbolLand_1',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.reelJustTouched],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            if (Number(args?.[0]) === 0) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbolLand_2',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.reelJustTouched],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            if (Number(args?.[0]) === 1) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbolLand_3',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.reelJustTouched],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            if (Number(args?.[0]) === 2) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbolLand_4',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.reelJustTouched],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            if (Number(args?.[0]) === 3) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'symbolLand_5',
                volume: SoundVolume.REEL_STOP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.reelJustTouched],
                        event: SoundEvent.CUSTOM,
                        callback: (_soundSettings: ISound, sound: Howl, args: any) => {
                            if (Number(args?.[0]) === 4) {
                                sound.play()
                            }
                        },
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'bigWin',
                volume: SoundVolume.BIG_WIN_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin, GameSignals.startBigWinLevel],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl, args: any) => {
                            SoundManager.playCommendationTierIfMatch(sound, args, 0)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.skipBigWin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'megaWin',
                volume: SoundVolume.BIG_WIN_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin, GameSignals.startBigWinLevel],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl, args: any) => {
                            SoundManager.playCommendationTierIfMatch(sound, args, 1)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.skipBigWin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'ultraWin',
                volume: SoundVolume.BIG_WIN_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin, GameSignals.startBigWinLevel],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl, args: any) => {
                            SoundManager.playCommendationTierIfMatch(sound, args, 2)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.skipBigWin],
                        event: SoundEvent.STOP,
                    },
                ],
            },
            {
                group: 'sounds',
                name: 'ultraMegaWin',
                volume: SoundVolume.BIG_WIN_TRIGGER,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.showBigWin, GameSignals.startBigWinLevel],
                        event: SoundEvent.CUSTOM,
                        callback: (_settings: ISound, sound: Howl, args: any) => {
                            SoundManager.playCommendationTierIfMatch(sound, args, 3)
                        },
                    },
                    {
                        onGameSignalsTriggered: [GameSignals.skipBigWin],
                        event: SoundEvent.STOP,
                    },
                ],
            },

            {
                group: 'sounds',
                name: 'symbol_drop_1',
                volume: SoundVolume.SYMBOLS_DROP,
                loop: false,
                events: [
                    {
                        onGameSignalsTriggered: [GameSignals.symbolsDrop],
                        event: SoundEvent.CUSTOM,
                        callback: (settings: ISound, sound: Howl, args: any) => {
                            gsap.to(this, {
                                duration: 0.1,
                                onComplete: () => {
                                    sound.play()
                                },
                            })
                        },
                    },
                ],
            },
        ]
    }
}
